import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { Search as SearchIcon, Chat as ChatIcon } from "@mui/icons-material";
import { motion } from "framer-motion";
import axiosInstance from "../../api/axiosInstance";
import base_url from "../../constants";
import useNotification from "../common/Notification";
import { WB_SX, WB_UI } from "../../ui/ux/uiTokens";

const Contacts = () => {
  const [contacts, setContacts] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 20,
    total: 0,
  });
  const [loading, setLoading] = useState(false);

  const [searchPhone, setSearchPhone] = useState("");
  const [searchGroup, setSearchGroup] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [userEngaged, setUserEngaged] = useState(false);
  const [userResponded, setUserResponded] = useState(false);

  const notify = useNotification();

  const fetchContacts = async (page = 1) => {
    setLoading(true);
    try {
      const params = {
        per_page: pagination.per_page,
        page,
        sort_order: sortOrder,
      };

      if (searchPhone.trim()) {
        params.search_phone = searchPhone.trim();
      }
      if (searchGroup.trim()) {
        params.search_group = searchGroup.trim();
      }
      if (userEngaged) {
        params.user_engaged = true;
      }
      if (userResponded) {
        params.user_responded = true;
      }

      const res = await axiosInstance.get(
        `${base_url}/whatsapp-business/contacts`,
        { params }
      );

      if (res.data?.success) {
        const data = res.data.data || {};
        setContacts(Array.isArray(data.contacts) ? data.contacts : []);
        setPagination(
          data.pagination || {
            current_page: page,
            last_page: 1,
            per_page: params.per_page,
            total: 0,
          }
        );
      } else {
        setContacts([]);
        setPagination((prev) => ({ ...prev, total: 0 }));
        notify(
          res.data?.message || "Failed to load contacts",
          "error"
        );
      }
    } catch (e) {
      notify(
        e.response?.data?.message || "Failed to load contacts",
        "error"
      );
      setContacts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortOrder]);

  const handleApplyFilters = () => {
    fetchContacts(1);
  };

  const handleResetFilters = () => {
    setSearchPhone("");
    setSearchGroup("");
    setSortOrder("asc");
    setUserEngaged(false);
    setUserResponded(false);
    fetchContacts(1);
  };

  const handlePageChange = (direction) => {
    const next =
      direction === "next"
        ? pagination.current_page + 1
        : pagination.current_page - 1;
    if (next < 1 || next > pagination.last_page) return;
    fetchContacts(next);
  };

  const hasContacts = contacts.length > 0;

  return (
    <Box sx={WB_SX.page}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={WB_SX.pageTitle}
          >
            Contacts
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View contacts across all WhatsApp Business groups.
          </Typography>
        </Box>
        {pagination.total > 0 && (
          <Chip
            size="small"
            label={`Total: ${pagination.total}`}
          />
        )}
      </Box>

      <Card
        elevation={0}
        sx={{
          mb: WB_UI.spacing.sectionGap,
          ...WB_SX.card,
        }}
      >
        <CardContent sx={WB_SX.cardContent}>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1.5,
              mb: 2,
              alignItems: "center",
            }}
          >
            <TextField
              size="small"
              label="Search phone"
              placeholder="e.g. 919876543210"
              value={searchPhone}
              onChange={(e) => setSearchPhone(e.target.value)}
              InputProps={{
                startAdornment: (
                  <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
                ),
              }}
              sx={{ minWidth: { xs: "100%", sm: 220 } }}
            />
            <TextField
              size="small"
              label="Search group"
              placeholder="Group name"
              value={searchGroup}
              onChange={(e) => setSearchGroup(e.target.value)}
              sx={{ minWidth: { xs: "100%", sm: 200 } }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Sort by first added</InputLabel>
              <Select
                value={sortOrder}
                label="Sort by first added"
                onChange={(e) => setSortOrder(e.target.value)}
              >
                <MenuItem value="asc">Oldest first</MenuItem>
                <MenuItem value="desc">Newest first</MenuItem>
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Checkbox
                  checked={userEngaged}
                  onChange={(e) => setUserEngaged(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Typography variant="body2">
                  Only engaged
                </Typography>
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={userResponded}
                  onChange={(e) => setUserResponded(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Typography variant="body2">
                  Only responded
                </Typography>
              }
            />

            <Box sx={{ flexGrow: 1 }} />

            <Button
              variant="contained"
              size="small"
              onClick={handleApplyFilters}
              disabled={loading}
              sx={WB_SX.secondaryButton}
            >
              {loading ? "Loading..." : "Apply filters"}
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleResetFilters}
              disabled={loading}
              sx={WB_SX.secondaryButton}
            >
              Reset
            </Button>
          </Box>

          {loading && !hasContacts ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : !hasContacts ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ py: 3, textAlign: "center" }}
            >
              No contacts found with the current filters.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        ...WB_SX.tableHeadCell,
                      }}
                    >
                      Phone Number
                    </TableCell>
                    <TableCell
                      sx={{
                        ...WB_SX.tableHeadCell,
                      }}
                    >
                      Contact Name
                    </TableCell>
                    <TableCell
                      sx={{
                        ...WB_SX.tableHeadCell,
                      }}
                    >
                      Groups
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        ...WB_SX.tableHeadCell,
                      }}
                    >
                      Groups Count
                    </TableCell>
                    <TableCell
                      sx={{
                        ...WB_SX.tableHeadCell,
                      }}
                    >
                      First Added
                    </TableCell>
                    <TableCell
                      sx={{
                        ...WB_SX.tableHeadCell,
                      }}
                    >
                      Last Updated
                    </TableCell>
                    <TableCell
                      sx={{
                        ...WB_SX.tableHeadCell,
                      }}
                    >
                      Last Sent
                    </TableCell>
                    <TableCell
                      sx={{
                        ...WB_SX.tableHeadCell,
                      }}
                    >
                      Last Received
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        ...WB_SX.tableHeadCell,
                      }}
                    >
                      Engaged
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        ...WB_SX.tableHeadCell,
                      }}
                    >
                      Responded
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        ...WB_SX.tableHeadCell,
                      }}
                    >
                      View Chats
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {contacts.map((c, idx) => (
                    <TableRow
                      key={c.phone_number + idx}
                      component={motion.tr}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.2, delay: idx * 0.01 }}
                      sx={WB_SX.tableRow}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {c.phone_number || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {c.contact_person_name || "Not available"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 0.5,
                          }}
                        >
                          {(c.groups || []).map((gName, gIdx) => (
                            <Chip
                              key={gName + gIdx}
                              size="small"
                              label={gName}
                              sx={{
                                fontWeight: 600,
                                borderRadius: 2,
                                backgroundColor:
                                  gIdx % 3 === 0
                                    ? "rgba(25,118,210,0.08)"
                                    : gIdx % 3 === 1
                                    ? "rgba(46,125,50,0.08)"
                                    : "rgba(156,39,176,0.08)",
                                color:
                                  gIdx % 3 === 0
                                    ? "primary.main"
                                    : gIdx % 3 === 1
                                    ? "success.main"
                                    : "secondary.main",
                              }}
                            />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        {c.groups_count ?? (c.groups ? c.groups.length : 0)}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {c.first_added_at
                            ? new Date(c.first_added_at).toLocaleString()
                            : "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {c.last_updated_at
                            ? new Date(c.last_updated_at).toLocaleString()
                            : "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {c.last_message_sent_at
                            ? new Date(
                                c.last_message_sent_at
                              ).toLocaleString()
                            : "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {c.last_message_received_at
                            ? new Date(
                                c.last_message_received_at
                              ).toLocaleString()
                            : "—"}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          size="small"
                          label={c.user_engaged ? "Yes" : "No"}
                          color={c.user_engaged ? "success" : "default"}
                          sx={{ fontWeight: 600, borderRadius: 2 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          size="small"
                          label={c.user_responded ? "Yes" : "No"}
                          color={c.user_responded ? "primary" : "default"}
                          sx={{ fontWeight: 600, borderRadius: 2 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() =>
                            window.open(
                              `/whatsapp-business/chat-window/contact/${encodeURIComponent(
                                c.phone_number || ""
                              )}`,
                              "_blank",
                              "noopener,noreferrer"
                            )
                          }
                        >
                          <ChatIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {pagination.last_page > 1 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 1.5,
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", fontWeight: 500 }}
              >
                Page {pagination.current_page} of {pagination.last_page}
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={pagination.current_page <= 1 || loading}
                  onClick={() => handlePageChange("prev")}
                  sx={{ textTransform: "none", borderRadius: 2 }}
                >
                  Previous
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={
                    pagination.current_page >= pagination.last_page || loading
                  }
                  onClick={() => handlePageChange("next")}
                  sx={{ textTransform: "none", borderRadius: 2 }}
                >
                  Next
                </Button>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Contacts;

